export type MediaPreviewType =
  | 'none'
  | 'youtube'
  | 'spotify'
  | 'soundcloud'
  | 'audio'
  | 'video'
  | 'direct-link';

export interface MediaPreviewInfo {
  type: MediaPreviewType;
  embedUrl?: string;
  rawUrl?: string;
  isEmbeddable: boolean;
  requiresIframe: boolean;
}

const AUDIO_EXTENSIONS = ['.mp3', '.wav', '.m4a', '.aac', '.ogg', '.flac'];
const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.webm', '.mkv', '.avi', '.m4v'];

const extractYouTubeId = (url: string): string | null => {
  try {
    const normalized = new URL(url);
    const host = normalized.hostname.replace('www.', '');

    if (host === 'youtu.be') {
      return normalized.pathname.split('/')[1] || null;
    }

    if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (normalized.pathname === '/watch') {
        return normalized.searchParams.get('v');
      }

      if (normalized.pathname.startsWith('/shorts/')) {
        return normalized.pathname.split('/')[2] || null;
      }

      const segments = normalized.pathname.split('/');
      const embedIndex = segments.indexOf('embed');
      if (embedIndex >= 0 && segments[embedIndex + 1]) {
        return segments[embedIndex + 1];
      }
    }
  } catch (error) {
    return null;
  }
  return null;
};

const buildYouTubeEmbed = (videoId: string): string =>
  `https://www.youtube.com/embed/${videoId}`;

const buildSpotifyEmbed = (url: string): string | null => {
  try {
    const normalized = new URL(url);
    if (!normalized.hostname.includes('spotify.com')) {
      return null;
    }

    const parts = normalized.pathname.split('/').filter(Boolean);
    if (parts.length < 2) {
      return null;
    }

    const embedPath = ['embed', parts[0], parts[1]].join('/');
    return `https://open.spotify.com/${embedPath}`;
  } catch (error) {
    return null;
  }
};

const hasExtension = (url: string, extensions: string[]): boolean => {
  const lower = url.toLowerCase();
  return extensions.some((ext) => lower.includes(ext));
};

export const detectMediaPreview = (inputUrl?: string | null): MediaPreviewInfo => {
  if (!inputUrl) {
    return {
      type: 'none',
      isEmbeddable: false,
      requiresIframe: false,
    };
  }

  const url = inputUrl.trim();
  if (!url) {
    return {
      type: 'none',
      isEmbeddable: false,
      requiresIframe: false,
    };
  }

  const youtubeId = extractYouTubeId(url);
  if (youtubeId) {
    return {
      type: 'youtube',
      embedUrl: `${buildYouTubeEmbed(youtubeId)}?autoplay=1&mute=0&rel=0`,
      rawUrl: url,
      isEmbeddable: true,
      requiresIframe: true,
    };
  }

  if (url.includes('open.spotify.com')) {
    const embedUrl = buildSpotifyEmbed(url);
    if (embedUrl) {
      return {
        type: 'spotify',
        embedUrl: `${embedUrl}?utm_source=generative_ai`,
        rawUrl: url,
        isEmbeddable: true,
        requiresIframe: true,
      };
    }
  }

  if (url.includes('soundcloud.com')) {
    const embedUrl = `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&auto_play=true&hide_related=false&show_comments=true&show_user=true&show_reposts=false`;
    return {
      type: 'soundcloud',
      embedUrl,
      rawUrl: url,
      isEmbeddable: true,
      requiresIframe: true,
    };
  }

  if (hasExtension(url, AUDIO_EXTENSIONS)) {
    return {
      type: 'audio',
      embedUrl: url,
      rawUrl: url,
      isEmbeddable: true,
      requiresIframe: false,
    };
  }

  if (hasExtension(url, VIDEO_EXTENSIONS)) {
    return {
      type: 'video',
      embedUrl: url,
      rawUrl: url,
      isEmbeddable: true,
      requiresIframe: false,
    };
  }

  return {
    type: 'direct-link',
    rawUrl: url,
    isEmbeddable: false,
    requiresIframe: false,
  };
};

